/**
 * Lazy-loads Roboto TTFs from /public/fonts and registers them with a jsPDF
 * document so doc.context2d can render text with the same font used on the
 * live canvas. Base64 payloads are cached in-module after first fetch to
 * avoid redownload on subsequent exports.
 */

const VARIANTS = [
    { file: 'Roboto-Regular.ttf',    style: 'normal',     weight: 'normal' },
    { file: 'Roboto-Bold.ttf',       style: 'normal',     weight: 'bold'   },
    { file: 'Roboto-Italic.ttf',     style: 'italic',     weight: 'normal' },
    { file: 'Roboto-BoldItalic.ttf', style: 'italic',     weight: 'bold'   }
]

let loadPromise = null
const cache = {}

async function fetchBase64(file) {
    if (cache[file]) return cache[file]
    const res = await fetch(`/fonts/${file}`)
    if (!res.ok) throw new Error(`Failed to load ${file}: ${res.status}`)
    const buf = await res.arrayBuffer()
    // Chunked btoa to avoid call-stack issues on large binaries
    const bytes = new Uint8Array(buf)
    let binary = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
    }
    cache[file] = btoa(binary)
    return cache[file]
}

async function loadAll() {
    if (!loadPromise) {
        loadPromise = Promise.all(VARIANTS.map(v => fetchBase64(v.file))).catch(err => {
            loadPromise = null
            throw err
        })
    }
    return loadPromise
}

/**
 * Register Roboto (regular/bold/italic/bold-italic) with a jsPDF document.
 * Safe to call multiple times; fonts are only fetched once across the session.
 * On failure the PDF falls back to Helvetica silently.
 *
 * @param {import('jspdf').jsPDF} doc
 */
export async function ensureRobotoFont(doc) {
    try {
        await loadAll()
        for (const v of VARIANTS) {
            doc.addFileToVFS(v.file, cache[v.file])
            doc.addFont(v.file, 'Roboto', v.style, v.weight)
        }
        return true
    } catch (err) {
        console.warn('Roboto font registration failed; PDF will use Helvetica.', err)
        return false
    }
}
